import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { EllipsisVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import { DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import ScrollArea from "../ui/shared/ScrollArea";
import clsx from "clsx";
import { useContacts } from "@/hooks/useContacts";
import { useAssistants } from "@/hooks/useAssistants";
import type { Database } from "@/lib/supabase";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getDisplayName } from "@/utils/contactUtils";
import { useTeam } from "@/hooks/useTeam";
import { clampAttributionIdForSave } from "@/lib/chatContactScope";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ContactEditProps {
  contact: AccountContact;
  styles?: string;
  onContactDeleted?: () => void;
  onContactUpdated?: () => void;
}

const ContactEdit: React.FC<ContactEditProps> = ({
  contact,
  styles = "w-9 h-9 border-0 bg-transparent hover:bg-hover transition-colors duration-200 rounded-[10px]",
  onContactDeleted,
  onContactUpdated,
}) => {
  const { t } = useTranslation("crm");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { updateContact, deleteContact, refetch, setContacts, setError: setContactsError } = useContacts();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const [attributionSearch, setAttributionSearch] = useState('');

  const canAssignToOthers = user?.can_assign_contacts === true || user?.type === 'agency_user';
  const lockedToOtherUser =
    !canAssignToOthers &&
    !!contact.attribution_id &&
    contact.attribution_id !== user?.auth_user_id;

  const [formData, setFormData] = useState({
    name: contact.name || '',
    email: contact.email || '',
    phone: contact.phone || '',
    account_assistant_id: contact.account_assistant_id || '',
    assistant_is_active: contact.assistant_is_active || false,
    attribution_id: contact.attribution_id || ''
  });

  const handleFormChange = (field: string, value: any) => {
    // Se for o campo de assistente, ativar automaticamente quando selecionado
    if (field === 'account_assistant_id') {
      const isAssistantSelected = value && value !== 'none' && value !== '';
      setFormData(prev => ({
        ...prev,
        [field]: value,
        assistant_is_active: isAssistantSelected
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Nome do contato é obrigatório' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Force loading state on contacts list
      setContactsError(null);

      const rawAttribution = formData.attribution_id === 'none' || formData.attribution_id === ''
        ? null
        : formData.attribution_id;
      // Se o usuário não pode reatribuir e o contato já estava atribuído a outro,
      // preservar a atribuição original (o select fica disabled, mas defesa em profundidade).
      const safeAttribution = lockedToOtherUser
        ? contact.attribution_id ?? null
        : clampAttributionIdForSave(rawAttribution, user ?? null);

      const updateData = {
        name: formData.name,
        display_name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        account_assistant_id: formData.account_assistant_id === 'none' || formData.account_assistant_id === '' ? null : formData.account_assistant_id,
        assistant_is_active: formData.assistant_is_active,
        attribution_id: safeAttribution,
      };

      // Update directly in database to ensure immediate refresh
      if (!supabase || !user) {
        throw new Error(t("contactEdit.notConfigured"));
      }

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update(updateData)
        .eq('id', contact.id)
        .eq('account_id', user.account_id);

      if (updateError) {
        throw new Error(t("contactEdit.updateError", { message: updateError.message }));
      }
      
      console.log('✅ [ContactEdit] Contato atualizado com sucesso, forçando refresh da listagem...');
      
      setMessage({ type: 'success', text: 'Contato atualizado com sucesso!' });
      
      await refetch();
      onContactUpdated?.();
      
      setTimeout(() => {
        setOpen(false);
      }, 1500);
      
    } catch (error) {
      console.error('❌ [ContactEdit] Erro ao atualizar contato:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Erro ao atualizar contato' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t("contactEdit.confirmDelete"))) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Force loading state on contacts list
      setContactsError(null);
      
      // Delete directly in database to ensure immediate refresh
      if (!supabase || !user) {
        throw new Error(t("contactEdit.notConfigured"));
      }

      // Bloquear exclusão se houver oportunidade no CRM associada ao contato
      const { count: opportunitiesCount, error: oppCheckError } = await supabase
        .from('account_opportunities')
        .select('id', { count: 'exact', head: true })
        .eq('contact_id', contact.id)
        .eq('account_id', user.account_id);

      if (oppCheckError) {
        throw new Error(t("contactEdit.checkOpportunitiesError", { message: oppCheckError.message }));
      }

      if ((opportunitiesCount ?? 0) > 0) {
        const blockMessage = 'Não é possível excluir este contato porque ele possui oportunidade(s) no CRM. Remova as oportunidades antes de excluir o contato.';
        toast.error(blockMessage);
        setMessage({ type: 'error', text: blockMessage });
        setOpen(false);
        return;
      }

      const { error: deleteError } = await supabase
        .from('account_contacts')
        .delete()
        .eq('id', contact.id)
        .eq('account_id', user.account_id);

      if (deleteError) {
        throw new Error(t("contactEdit.deleteError", { message: deleteError.message }));
      }
      
      console.log('✅ [ContactEdit] Contato deletado com sucesso, forçando refresh da listagem...');
      
      toast.success(t("contactEdit.deleted"));
      await refetch();
      onContactDeleted?.();
      setOpen(false);
      
    } catch (error) {
      console.error('❌ [ContactEdit] Erro ao deletar contato:', error);
      const errMessage = error instanceof Error ? error.message : 'Erro ao deletar contato';
      toast.error(errMessage);
      setMessage({ type: 'error', text: errMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = async (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Reset form data when opening
      setFormData({
        name: contact.name || getDisplayName(contact),
        email: contact.email || '',
        phone: contact.phone || '',
        account_assistant_id: contact.account_assistant_id || 'none',
        assistant_is_active: contact.assistant_is_active || false,
        attribution_id: contact.attribution_id || 'none'
      });
      setMessage(null);
      setAttributionSearch('');
    } else {
      // Refresh contacts list when closing modal
      console.log('🔄 [ContactEdit] Modal fechado, atualizando listagem de contatos...');
      await refetch();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className={clsx("flex justify-center items-center rounded-[10px]", styles)}>
          <EllipsisVertical size={18} className="text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="!bg-card p-1 w-auto min-w-[140px] flex flex-col gap-1 border-border border shadow-md">
        <button
          onClick={() => {
            setOpen(false);
            navigate(`/crm/${contact.id}`);
          }}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-hover min-h-[29px] text-foreground transition-colors duration-200"
        >
          {t("contactEdit.detail")}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            navigate(`/chat/${contact.id}`);
          }}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-hover min-h-[29px]"
        >
          {t("contactEdit.goToChat")}
        </button>
        <Dialog>
          <DialogTrigger asChild>
            <div className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-hover min-h-[29px]">
              {t("common.edit")}
            </div>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 border-border bg-card flex-col">
            <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
              <div className="w-full flex flex-col gap-4">
                {message && (
                  <div className={`p-3 rounded-md ${
                    message.type === 'success' 
                      ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
                      : 'bg-red-500/20 border border-red-500/30 text-red-300'
                  }`}>
                    {message.text}
                  </div>
                )}

                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-foreground">{t("contactEdit.title")}</DialogTitle>
                </DialogHeader>

                {/* Assistente Section */}
                <div className="w-full gap-4 flex flex-col bg-card-muted rounded-[12px] border border-border p-3">
                  <div className="w-full flex justify-between gap-2">
                    <p className="text-[14px] font-semibold text-foreground">
                      {t("common.assistant")} <br /> {t("common.active")}
                    </p>
                    <Switch
                      checked={formData.assistant_is_active || false}
                      onCheckedChange={(checked) => {
                        handleFormChange('assistant_is_active', checked);
                      }}
                      activeColor="data-[state=checked]:bg-[#16A34A] dark:data-[state=checked]:bg-[#22C55E]"
                    />
                  </div>
                  
                  <Select 
                    value={formData.account_assistant_id} 
                    onValueChange={(value) => handleFormChange('account_assistant_id', value)}
                  >
                    <SelectTrigger className="w-full rounded-[12px] bg-card">
                      <SelectValue placeholder={t("common.pickAssistant")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">{t("common.noAssistant")}</SelectItem>
                        {assistants.filter(a => a.is_active).map((assistant) => (
                          <SelectItem key={assistant.id} value={assistant.id}>
                            {assistant.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <form className="w-full flex flex-col gap-4">
                  {/* Nome */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">{t("common.nameRequired")}</Label>
                    <Input
                      placeholder={t("common.contactNamePlaceholder")}
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                      type="text"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="email">{t("common.email")}</Label>
                    <Input
                      placeholder="email@exemplo.com"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                      type="email"
                    />
                  </div>

                  {/* Telefone */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="phone">{t("common.phone")}</Label>
                    <Input
                      placeholder="(11) 99999-9999"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                      type="tel"
                    />
                  </div>

                  {/* Responsável pelo Contato */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="attribution">{t("common.assignTo")}</Label>
                    <Select
                      value={formData.attribution_id}
                      onValueChange={(value) => handleFormChange('attribution_id', value)}
                      onOpenChange={(open) => {
                        if (!open) setAttributionSearch('');
                      }}
                      disabled={!canAssignToOthers}
                    >
                      <SelectTrigger className="bg-input text-[14px] w-full placeholder:text-muted-foreground text-foreground !h-[40px] border focus:ring-primary/30 border-border">
                        <SelectValue placeholder={t("common.pickOwner")} />
                      </SelectTrigger>
                      <SelectContent>
                        {canAssignToOthers && (
                          <div className="px-2 pb-2">
                            <Input
                              placeholder={t("common.searchOwner")}
                              value={attributionSearch}
                              onChange={(e) => setAttributionSearch(e.target.value)}
                              className="h-8 bg-input border-border text-foreground"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                        <SelectGroup>
                          {canAssignToOthers && (
                            <SelectItem value="none">{t("common.noOwner")}</SelectItem>
                          )}
                          {members
                            .filter(m => m.is_active && m.auth_user_id)
                            .filter(m =>
                              canAssignToOthers ? true : m.auth_user_id === user?.auth_user_id
                            )
                            .filter(m => {
                              if (!attributionSearch) return true;
                              const searchLower = attributionSearch.toLowerCase();
                              const name = (m.name || '').toLowerCase();
                              const email = (m.email || '').toLowerCase();
                              return name.includes(searchLower) || email.includes(searchLower);
                            })
                            .map((member) => (
                              <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                                {member.name || member.email}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {lockedToOtherUser && (
                      <p className="text-xs text-amber-400/90">
                        {t("contactEdit.assignedElsewhere")}
                      </p>
                    )}
                    {!canAssignToOthers && !lockedToOtherUser && (
                      <p className="text-xs text-muted-foreground">
                        {t("common.onlySelf")}
                      </p>
                    )}
                  </div>

                  <div className="w-full flex justify-end gap-3 mt-4">
                    <Button 
                      type="button"
                      onClick={handleSave}
                      disabled={loading}
                      className="min-w-[120px]"
                    >
                      {loading ? 'Salvando...' : 'Atualizar'}
                    </Button>
                  </div>
                </form>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
        <button 
          onClick={handleDelete}
          disabled={loading}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-hover min-h-[29px] text-red-400"
        >
          {loading ? 'Removendo...' : 'Remover'}
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default ContactEdit;